import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import { AppFilterBar, AppFilterInput, AppFilterSelect } from '../../components/filters';
import SortableTh from '../../components/common/SortableTh';
import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import { useServerTableSort } from '../../hooks/useTableSort';
import secteursService from '../../services/secteursService';
import { formatCa } from '../../utils/secteursMapUtils';
import { ROUTES } from '../../config/routes';
import { AppAlert, AppBadge, AppButton, AppPagination, AppSelect, AppSpinner } from '../../components/ui';
import '../../styles/secteurs-entreprises.scss';

const EMPTY_FILTERS = {
  nom: '',
  codePostal: '',
  departement: [],
  secteur: [],
  sectionActivite: [],
  trancheEffectif: [],
  caMin: '',
  caMax: '',
};

const SecteursEntreprisesPage = () => {
  const [entreprises, setEntreprises] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState(null);
  const [pageSizes, setPageSizes] = useState([25, 50, 100, 150, 200]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const { sort, toggleSort, getSortIcon } = useServerTableSort('nomRaisonSociale', 'asc');
  const [filterOptions, setFilterOptions] = useState({
    departements: [],
    sectionsNaf: {},
    tranchesEffectif: {},
    secteurs: [],
  });

  useEffect(() => {
    let cancelled = false;

    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const data = await secteursService.getFilterOptions();
        if (!cancelled) {
          setFilterOptions({
            departements: data.departements ?? [],
            sectionsNaf: data.sectionsNaf ?? {},
            tranchesEffectif: data.tranchesEffectif ?? {},
            secteurs: data.secteurs ?? [],
          });
          if (data.page_sizes?.length) setPageSizes(data.page_sizes);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur de chargement des filtres');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };

    loadOptions();
    return () => { cancelled = true; };
  }, []);

  const loadEntreprises = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit,
        ...appliedFilters,
        sortField: sort.key,
        sortDirection: sort.direction,
      };
      const result = await secteursService.getEntreprises(params);
      setEntreprises(result.entreprises);
      setPagination(result.pagination);
      setStats(result.stats);
      if (result.pageSizes?.length) setPageSizes(result.pageSizes);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedFilters, sort.key, sort.direction]);

  useEffect(() => {
    loadEntreprises();
  }, [loadEntreprises]);

  const handleSort = (key) => {
    toggleSort(key);
    setPage(1);
  };

  const setFilterArray = (key) => (values) => {
    setDraftFilters((f) => ({ ...f, [key]: values }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const secteurOptions = useMemo(
    () => filterOptions.secteurs.map((s) => ({
      value: String(s.id),
      label: s.nomCommercial || s.nomSecteur,
    })),
    [filterOptions.secteurs],
  );

  const trancheEffectifOptions = useMemo(
    () => Object.entries(filterOptions.tranchesEffectif).map(([code, libelle]) => ({
      value: code,
      label: libelle,
    })),
    [filterOptions.tranchesEffectif],
  );

  const sectionActiviteOptions = useMemo(
    () => Object.entries(filterOptions.sectionsNaf).map(([code, libelle]) => ({
      value: code,
      label: `${code} - ${libelle}`,
    })),
    [filterOptions.sectionsNaf],
  );

  const etatLabel = (etat) => {
    if (etat === 'A') return <AppBadge variant="success">Actif</AppBadge>;
    if (etat === 'C') return <AppBadge variant="danger">Cessé</AppBadge>;
    return <AppBadge variant="secondary">Inconnu</AppBadge>;
  };

  const filterScope = useMemo(() => {
    const f = appliedFilters;
    const parts = [];
    if (f.nom) parts.push(`Nom « ${f.nom} »`);
    if (f.codePostal) parts.push(`CP ${f.codePostal}`);
    if (f.departement?.length) parts.push(`Dép. ${f.departement.join(', ')}`);
    if (f.secteur?.length) {
      const labels = f.secteur.map((id) => {
        const s = filterOptions.secteurs.find((item) => String(item.id) === String(id));
        return s?.nomCommercial || s?.nomSecteur || id;
      });
      parts.push(labels.join(', '));
    }
    if (f.sectionActivite?.length) parts.push(`NAF ${f.sectionActivite.join(', ')}`);
    if (f.trancheEffectif?.length) parts.push('Effectifs filtrés');
    if (f.caMin || f.caMax) {
      parts.push(`CA ${f.caMin || '…'} – ${f.caMax || '…'}`);
    }
    return parts.length ? parts.join(' · ') : 'Toutes les entreprises actives';
  }, [appliedFilters, filterOptions.secteurs]);

  return (
    <MasterLayout>
      <Breadcrumb title="Entreprises locales" />

      <div className="mb-3">
        <AppButton
          size="sm"
          variant="outline-primary-600"
          className="mb-2"
          onClick={() => setShowFilters((v) => !v)}
        >
          {showFilters ? 'Masquer les filtres' : 'Afficher les filtres'}
        </AppButton>
        {showFilters && (
          optionsLoading ? (
            <div className="text-center py-3">
              <AppSpinner size="sm" centered={false} />
            </div>
          ) : (
            <form onSubmit={handleSearch}>
              <AppFilterBar
                onReset={handleClearFilters}
                showReset={false}
              >
                <AppFilterInput
                  id="nom"
                  label="Nom de l'entreprise"
                  type="text"
                  col="col-lg-4 col-md-6"
                  value={draftFilters.nom}
                  onChange={(val) => setDraftFilters((f) => ({ ...f, nom: val }))}
                  placeholder="Rechercher par nom..."
                />
                <AppFilterInput
                  id="codePostal"
                  label="Code postal"
                  type="text"
                  col="col-lg-2 col-md-6"
                  value={draftFilters.codePostal}
                  onChange={(val) => setDraftFilters((f) => ({ ...f, codePostal: val }))}
                  placeholder="75001"
                />
                <AppFilterInput
                  id="caMin"
                  label="CA min (€)"
                  type="number"
                  col="col-lg-3 col-md-6"
                  value={draftFilters.caMin}
                  onChange={(val) => setDraftFilters((f) => ({ ...f, caMin: val }))}
                  placeholder="0"
                />
                <AppFilterInput
                  id="caMax"
                  label="CA max (€)"
                  type="number"
                  col="col-lg-3 col-md-6"
                  value={draftFilters.caMax}
                  onChange={(val) => setDraftFilters((f) => ({ ...f, caMax: val }))}
                  placeholder="1000000"
                />
                <AppFilterSelect
                  id="sectionActivite"
                  label="Secteur d'activité"
                  col="col-lg-4 col-md-6"
                  value={draftFilters.sectionActivite}
                  onChange={setFilterArray('sectionActivite')}
                  options={sectionActiviteOptions}
                  placeholder="Tous les secteurs d'activité"
                />
                <AppFilterSelect
                  id="secteur"
                  label="Secteur géographique"
                  col="col-lg-4 col-md-6"
                  value={draftFilters.secteur.map(String)}
                  onChange={setFilterArray('secteur')}
                  options={secteurOptions}
                  placeholder="Tous les secteurs"
                />
                <AppFilterSelect
                  id="trancheEffectif"
                  label="Tranche d'effectif"
                  col="col-lg-4 col-md-6"
                  value={draftFilters.trancheEffectif}
                  onChange={setFilterArray('trancheEffectif')}
                  options={trancheEffectifOptions}
                  placeholder="Toutes les tranches"
                />
                <AppFilterSelect
                  id="departement"
                  label="Département"
                  col="col-lg-4 col-md-6"
                  value={draftFilters.departement}
                  onChange={setFilterArray('departement')}
                  options={filterOptions.departements.map((d) => ({ value: d, label: d }))}
                  placeholder="Tous les départements"
                />
                <div className="col-auto d-flex gap-2 align-items-end pb-1">
                  <AppButton type="submit" size="sm">
                    Rechercher
                  </AppButton>
                  <AppButton type="button" size="sm" variant="outline-secondary" onClick={handleClearFilters}>
                    Effacer
                  </AppButton>
                </div>
              </AppFilterBar>
            </form>
          )
        )}
      </div>

      <div className="secteurs-entreprises-filter-stats">
        <span className="secteurs-entreprises-filter-stats__scope">{filterScope}</span>
        <div className="secteurs-entreprises-filter-stats__metrics">
          <div>
            <span>Entreprises</span>
            <strong>{loading ? '—' : (stats?.entreprises ?? pagination.total).toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Secteurs géo</span>
            <strong>{loading ? '—' : (stats?.secteurs ?? '—')}</strong>
          </div>
          <div>
            <span>Secteurs d’activité</span>
            <strong>{loading ? '—' : (stats?.sections ?? '—')}</strong>
          </div>
          <div>
            <span>Départements</span>
            <strong>{loading ? '—' : (stats?.departements ?? '—')}</strong>
          </div>
        </div>
      </div>

      {error && <AppAlert>{error}</AppAlert>}

      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-end align-items-center gap-2 py-2">
          <AppSelect
            size="sm"
            className="w-auto"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            aria-label="Résultats par page"
          >
            {pageSizes.map((s) => (
              <option key={s} value={s}>{s} / page</option>
            ))}
          </AppSelect>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : (
            <StickyTableResponsive>
              <table className="table bordered-table mb-0 secteurs-entreprises-table">
                <colgroup>
                  <col className="col-nom" />
                  <col className="col-adresse" />
                  <col className="col-activite" />
                </colgroup>
                <thead>
                  <tr>
                    <SortableTh sortKey="nomRaisonSociale" label="Nom" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="adresse" label="Adresse" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="departement" label="Dép." sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="secteurNom" label="Secteur géographique" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="sectionActivitePrincipale" label="Secteur d'activité" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="trancheEffectifSalarie" label="Effectifs" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="chiffreAffaires" label="CA" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="etatAdministratif" label="État" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="siret" label="SIRET" sort={sort} onSort={handleSort} getSortIcon={getSortIcon} />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entreprises.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-secondary-light py-4">
                        Aucune entreprise trouvée
                      </td>
                    </tr>
                  ) : (
                    entreprises.map((e) => (
                      <tr key={e.id || e.siret}>
                        <td className="cell-nom" title={[e.nomRaisonSociale, e.sigle].filter(Boolean).join(' — ')}>
                          <div className="secteurs-entreprises-table__clamp">
                            <span className="fw-medium">{e.nomRaisonSociale || '—'}</span>
                            {e.sigle && (
                              <><br /><small className="text-secondary-light">{e.sigle}</small></>
                            )}
                          </div>
                        </td>
                        <td
                          className="cell-adresse"
                        >
                          {e.adresse && <>{e.adresse}<br /></>}
                          {e.codePostal && e.libelleCommune
                            ? `${e.codePostal} ${e.libelleCommune}`
                            : (e.libelleCommune || '—')}
                        </td>
                        <td className="cell-dep">{e.departement || '—'}</td>
                        <td>
                          {e.secteurNom ? (
                            <KkpColorBadge color={e.secteurCouleur} vivid>
                              {e.secteurNom}
                            </KkpColorBadge>
                          ) : '—'}
                        </td>
                        <td className="cell-activite">
                          {e.sectionActivitePrincipale ? (
                            <AppBadge variant="info" className="secteurs-entreprises-table__activite-badge">
                              {e.sectionActivitePrincipale}
                              {' - '}
                              {filterOptions.sectionsNaf[e.sectionActivitePrincipale] || 'N/A'}
                            </AppBadge>
                          ) : '—'}
                        </td>
                        <td>
                          {e.trancheEffectifSalarie
                            ? (filterOptions.tranchesEffectif[e.trancheEffectifSalarie] || e.trancheEffectifSalarie)
                            : '—'}
                        </td>
                        <td>{formatCa(e.chiffreAffaires)}</td>
                        <td>{etatLabel(e.etatAdministratif)}</td>
                        <td>{e.siret || '—'}</td>
                        <td>
                          {e.id && (
                            <AppButton
                              as={Link}
                              to={ROUTES.secteurs.entrepriseDetail(e.id)}
                              size="sm"
                              variant="outline-primary-600"
                            >
                              Détail
                            </AppButton>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="card-footer">
            <AppPagination
              variant="simple"
              pagination={pagination}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </MasterLayout>
  );
};

export default SecteursEntreprisesPage;
